import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Upload, Trash2 } from 'lucide-react';
import { configuracionAPI, uploadAPI } from '../services/api';
import TipTapEditor from './TipTapEditor';
import { Card, Button, Input } from './ui';

// La firma se almacena junto a metodos_pago separada por este marcador.
// El PDF las divide al renderizar.
const FIRMA_SEPARATOR = '<!--FIRMA-->';

/**
 * DatosEmpresa
 * Configuración relacionada con identidad de la empresa y datos que
 * aparecen en el PDF de cotización: logo, razón social, métodos de pago,
 * firma de cierre. Ruta: /datos-empresa.
 *
 * Separado de ConfiguracionPanel (parámetros operativos: IGV, TC, rentabilidad,
 * integraciones) para que cada vista quede enfocada.
 */
const DatosEmpresa = ({ onConfigUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({});
    const [metodosPagoLocal, setMetodosPagoLocal] = useState('');
    const [firmaLocal, setFirmaLocal] = useState('');

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const configRes = await configuracionAPI.getAll();
            setConfig(configRes.data || {});
            const mpRaw = configRes.data?.metodos_pago || '';
            if (mpRaw.includes(FIRMA_SEPARATOR)) {
                const [mp, firma] = mpRaw.split(FIRMA_SEPARATOR);
                setMetodosPagoLocal(mp || '');
                setFirmaLocal(firma || '');
            } else {
                setMetodosPagoLocal(mpRaw);
                setFirmaLocal('');
            }
        } catch (err) {
            console.error('Error al cargar datos de empresa:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLocalChange = (clave, valor) => {
        setConfig(prev => ({ ...prev, [clave]: valor }));
    };

    // Guarda inmediato — usado por logo (upload requiere persistir al momento).
    const handleImmediateSave = async (clave, valor) => {
        try {
            await configuracionAPI.update(clave, valor);
            setConfig(prev => {
                const newConfig = { ...prev, [clave]: valor };
                if (onConfigUpdate) onConfigUpdate(newConfig);
                return newConfig;
            });
        } catch (err) {
            toast.error('Error al actualizar: ' + err.message);
        }
    };

    const guardarTodo = async () => {
        setSaving(true);
        try {
            const metodosPagoCombined = firmaLocal.trim()
                ? `${metodosPagoLocal}${FIRMA_SEPARATOR}${firmaLocal}`
                : metodosPagoLocal;

            const configToSave = {
                empresa_nombre: config.empresa_nombre || '',
                empresa_ruc: config.empresa_ruc || '',
                empresa_email: config.empresa_email || '',
                empresa_telefono: config.empresa_telefono || '',
                metodos_pago: metodosPagoCombined,
            };

            await configuracionAPI.updateMultiple(configToSave);

            const newConfig = { ...config, metodos_pago: metodosPagoCombined };
            setConfig(newConfig);
            if (onConfigUpdate) onConfigUpdate(newConfig);
            toast.success('Datos de empresa guardados correctamente');
        } catch (err) {
            toast.error('Error al guardar: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // Maneja el upload del logo (validacion + upload + guardado inmediato + cleanup anterior)
    const handleLogoUpload = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error('El archivo es demasiado grande. Máximo 10MB', { id: 'upload-logo' });
            return;
        }
        const allowedExts = ['.jpg', '.jpeg', '.png', '.svg', '.ico', '.webp'];
        const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!allowedExts.includes(ext)) {
            toast.error('Formato no permitido. Solo: JPG, PNG, SVG, ICO, WEBP', { id: 'upload-logo' });
            return;
        }
        try {
            toast.loading('Subiendo logo...', { id: 'upload-logo' });
            const logoAnterior = config.empresa_logo;
            if (logoAnterior && !logoAnterior.startsWith('data:') && !logoAnterior.startsWith('http')) {
                const filename = logoAnterior.split('/').pop();
                try {
                    await uploadAPI.deleteFile(filename);
                } catch (delErr) {
                    console.warn('No se pudo eliminar el logo anterior:', delErr);
                }
            }
            const result = await uploadAPI.uploadLogo(file);
            await handleImmediateSave('empresa_logo', result.url);
            toast.success('Logo subido correctamente', { id: 'upload-logo' });
        } catch (err) {
            const errorMsg = err.message || '';
            if (errorMsg.includes('FORMAT') || errorMsg.includes('formato')) {
                toast.error('Formato no permitido. Solo: JPG, PNG, SVG, ICO, WEBP', { id: 'upload-logo' });
            } else if (errorMsg.includes('SIZE') || errorMsg.includes('grande') || errorMsg.includes('10MB')) {
                toast.error('El archivo es demasiado grande. Máximo 10MB', { id: 'upload-logo' });
            } else {
                toast.error('Error al subir el logo: ' + errorMsg, { id: 'upload-logo' });
            }
        }
    };

    const handleLogoDelete = async () => {
        try {
            const logoAnterior = config.empresa_logo;
            if (logoAnterior && !logoAnterior.startsWith('data:') && !logoAnterior.startsWith('http')) {
                const filename = logoAnterior.split('/').pop();
                await uploadAPI.deleteFile(filename);
            }
            await handleImmediateSave('empresa_logo', '');
            toast.success('Logo eliminado');
        } catch (err) {
            toast.error('Error al eliminar: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando datos de empresa...</div>;
    }

    const logoSrc = config.empresa_logo
        ? (config.empresa_logo.startsWith('data:') || config.empresa_logo.startsWith('http')
            ? config.empresa_logo
            : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${config.empresa_logo}`)
        : null;

    return (
        <div className="flex flex-col gap-4">
            {/* Header de la pagina */}
            <div className="flex flex-col">
                <h2 style={{ margin: 0 }}>Datos de empresa</h2>
                <span className="text-sm text-muted mt-1">
                    Identidad y datos que aparecen en el PDF de cotización.
                </span>
            </div>

            {/* Fila 1: Logo + Datos generales en 2 columnas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Logo */}
                <Card>
                    <Card.Header title="Logo" />
                    <Card.Body>
                        <div className="flex items-center gap-4 flex-wrap">
                            {logoSrc ? (
                                <>
                                    <div className="logo-preview-frame">
                                        <img src={logoSrc} alt="Logo de la empresa" />
                                    </div>
                                    <button
                                        type="button"
                                        className="notif-action delete"
                                        onClick={handleLogoDelete}
                                    >
                                        <Trash2 size={14} aria-hidden="true" />
                                        <span>Eliminar logo</span>
                                    </button>
                                </>
                            ) : (
                                <label className="inline-flex">
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.svg,.ico,.webp"
                                        onChange={async (e) => {
                                            await handleLogoUpload(e.target.files[0]);
                                            e.target.value = '';
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-surface-2 hover:bg-line/60 text-text text-sm font-medium cursor-pointer transition">
                                        <Upload size={14} />
                                        Subir logo
                                    </span>
                                </label>
                            )}
                        </div>
                        <p className="text-[11px] text-muted mt-3">
                            Formatos: JPG, PNG, SVG, ICO, WEBP. Máximo 10MB. Si no subes un logo, se usará el default del sistema.
                        </p>
                    </Card.Body>
                </Card>

                {/* Datos generales */}
                <Card>
                    <Card.Header title="Datos generales" />
                    <Card.Body>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                                id="emp-nombre"
                                label="Nombre"
                                type="text"
                                value={config.empresa_nombre || ''}
                                onChange={(e) => handleLocalChange('empresa_nombre', e.target.value)}
                            />
                            <Input
                                id="emp-ruc"
                                label="RUC"
                                type="text"
                                value={config.empresa_ruc || ''}
                                onChange={(e) => handleLocalChange('empresa_ruc', e.target.value)}
                            />
                            <Input
                                id="emp-email"
                                label="Email"
                                type="email"
                                value={config.empresa_email || ''}
                                onChange={(e) => handleLocalChange('empresa_email', e.target.value)}
                            />
                            <Input
                                id="emp-telefono"
                                label="Teléfono"
                                type="text"
                                value={config.empresa_telefono || ''}
                                onChange={(e) => handleLocalChange('empresa_telefono', e.target.value)}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>

            {/* Fila 2: Métodos de pago + Firma en 2 columnas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Metodos de pago */}
                <Card>
                    <Card.Header title="Métodos de pago" />
                    <Card.Body>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">Información de cuentas bancarias</label>
                            <TipTapEditor
                                value={metodosPagoLocal}
                                onChange={(html) => setMetodosPagoLocal(html)}
                            />
                            <p className="text-[11px] text-muted">Datos bancarios que aparecerán en el PDF de cotización.</p>
                        </div>
                    </Card.Body>
                </Card>

                {/* Firma */}
                <Card>
                    <Card.Header title="Firma" />
                    <Card.Body>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">Despedida y firma del responsable</label>
                            <TipTapEditor
                                value={firmaLocal}
                                onChange={(html) => setFirmaLocal(html)}
                            />
                            <p className="text-[11px] text-muted">Texto de cierre que aparecerá al final del PDF.</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>

            {/* Barra de accion al final */}
            <div className="flex justify-end gap-2 pt-2">
                <Button
                    variant="primary"
                    onClick={guardarTodo}
                    disabled={saving}
                >
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            </div>
        </div>
    );
};

export default DatosEmpresa;
